<template>
  <div class="donation-credit-calculator__wrapper" :class="language === 'fr' ? 'french-input' : ''">

    <!-- Province Selection -->
    <label class="donation-credit-calculator__labels" for="donation-credit-calculator__province-select">
      {{ translation('provinceLabel') }}
    </label>
    <select
      class="donation-credit-calculator__inputs"
      :class="!province ? 'disabled' : ''"
      id="donation-credit-calculator__province-select"
      v-model="province"
    >
      <option disabled value="">
        {{ translation('selectProvincePlaceholder') }}
      </option>
      <option v-for="(info, key) in provinces" :key="key" :value="key">
        {{ info.label[language] }}
      </option>
    </select>

    <!-- Frequency Selection -->
    <label v-if="frequencylist" class="donation-credit-calculator__labels" for="donation-credit-calculator__frequency-select">
      {{ translation('frequencyLabel') }}
    </label>
    <select
      class="donation-credit-calculator__inputs"
      id="donation-credit-calculator__frequency-select"
      v-model="frequencySelector"
      v-if="frequencylist"
    >
      <option
        v-for="key in parsedFrequencyArray"
        :key="key"
        :value="key"
        v-if="frequencyObject[key]"
      >
        {{ getFrequencyLabel(key) }}
      </option>
    </select>

    <!-- Donation amount input -->
    <label class="donation-credit-calculator__labels" for="donation-credit-calculator__donation-input">
      {{ translation('donationAmountLabel') }}
    </label>
    <div class="input-currency">
      <div>$</div>
    </div>
    <input
      class="donation-credit-calculator__inputs"
      id="donation-credit-calculator__donation-input"
      v-model="donationInput"
      type="text"
      inputmode="decimal"
      maxlength="7"
      min="0"
      autocomplete="off"
      pattern="\d+(\.\d{1,2})?"
      @input="handleInput"
      @keypress="preventNonNumeric"
      :placeholder="translation('donationPlaceholder')"
    />

    <!-- Calculate Button -->
    <button
      class="btn"
      :class="!isProvinceSelected || !donationInput || donationInput <= 0 ? 'btn--black btn--disabled' : 'btn--yellow'"
      :disabled="!isProvinceSelected || !donationInput || donationInput <= 0"
      @click="calculateCredits"
    >
      <span class="btn__content">
        <span class="btn__text">{{ translation('calculateButton') }}</span>
      </span>
    </button>

    <!-- Results -->
    <div class="donation-credit-calculator__results">
      <p class="title">
        {{ translation('resultsTitle') }}
        <toggle-tip
          :open="openTooltipIcon"
          :close="close"
          :description="translation('donationToolTip')"
        ></toggle-tip>
        <!-- :description="disclaimertooltip" -->
      </p>

      <div class="results__row">
        <p>{{ translation('totalDonatedLabel') }}</p>
        <p>{{ formatCurrency(totalDonated) }}</p>
      </div>
      <div class="results__row">
        <p>{{ translation('federalCreditLabel') }}</p>
        <p>{{ formatCurrency(federalCredit) }}</p>
      </div>
      <div class="results__row">
        <p>{{ translation('provincialCreditLabel') }}</p>
        <p>{{ formatCurrency(provincialCredit) }}</p>
      </div>
      <div class="results__row">
        <p class="total">{{ translation('totalCreditLabel') }}</p>
        <p class="total">{{ formatCurrency(totalCredit) }}</p>
      </div>
    </div>

    <!-- <p style="font-size: 16px">
      <b>Debug:</b><br><br>
      Actualprovince (comes from sitecore): {{ actualprovince }}<br>
      Province selected (default comes from sitecore): {{ province }}<br>
      
      Language: {{ language }}<br>
      Donation Inputed: {{ donationInput }}<br>
      Total Donated: {{ donation }}<br><br>
      
      Frequency list from prop: {{ frequencylist }}<br><br>
      Cleanned frequency list (parsedFrequencyArray): {{ parsedFrequencyArray }}<br><br>
      Full Frequency Object (frequencyObject): {{ frequencyObject }}<br><br>
      Selected Frequency: {{ frequencySelector }}<br><br>
      
      Federal Credit: {{ federalCredit }}<br>
      Provincial Credit: {{ provincialCredit }}<br>
      Total Credit: {{ totalCredit }}<br>
    </p> -->
  </div>
</template>

<script>

import { RegionStore } from '../../store/regionStore';
export default {
  props: {
    language: { type: String, required: true },
    actualprovince: { type: String, required: true },
    disclaimertooltip: { type: String, required: true },
    frequencylist: {
      type: [Array, String],
      required: true,
    }
  },
  data() {
    return {
      donation: 0,
      donationInput: null,
      showResults: false,
      province: '',
      locale: 'en-CA',
      provinces: {
        // calculations are ('base percentage' x 'first $200') + ('amount above $200' x 'over $200 percentage')
        'AB': { first: 0.60,   over: 0.21,   label: { en: 'Alberta', fr: 'Alberta' } },
        'BC': { first: 0.0506, over: 0.168,  label: { en: 'British Columbia', fr: 'Colombie-Britannique' } },
        'MB': { first: 0.108,  over: 0.174,  label: { en: 'Manitoba', fr: 'Manitoba' } },
        'NB': { first: 0.094,  over: 0.1795, label: { en: 'New Brunswick', fr: 'Nouveau-Brunswick' } },
        'NL': { first: 0.087,  over: 0.218,  label: { en: 'Newfoundland and Labrador', fr: 'Terre-Neuve-et-Labrador' } },
        'NS': { first: 0.0879, over: 0.21,   label: { en: 'Nova Scotia', fr: 'Nouvelle-Écosse' } },
        'ON': { first: 0.0505, over: 0.1116, label: { en: 'Ontario', fr: 'Ontario' } },
        'PE': { first: 0.0965, over: 0.1875, label: { en: 'Prince Edward Island', fr: 'Île-du-Prince-Édouard' } },
        'QC': { first: 0.20,   over: 0.24,   label: { en: 'Quebec', fr: 'Québec' } },
        'SK': { first: 0.105,  over: 0.145,  label: { en: 'Saskatchewan', fr: 'Saskatchewan' } },
        'NT': { first: 0.059,  over: 0.1405, label: { en: 'Northwest Territories', fr: 'Territoires du Nord-Ouest' } },
        'NU': { first: 0.04,   over: 0.115,  label: { en: 'Nunavut', fr: 'Nunavut' } },
        'YT': { first: 0.064,  over: 0.128,  label: { en: 'Yukon', fr: 'Yukon' } }
      },

      // this is the frequency object that will be used to calculate the donation amount based on the frequency selected
      // the multiplier is used to calculate the donation amount based on the frequency selected
      // the label is used to display the frequency in the dropdown
      frequencyObject: {
        'oneTime':      { multiplier: 1,  label: { en: 'One-time', fr: 'Ponctuel' } },
        'weekly':       { multiplier: 52, label: { en: 'Weekly', fr: 'Hebdomadaire' } },
        'biWeekly':     { multiplier: 26, label: { en: 'Bi-weekly', fr: 'Bimensuel' } },
        'monthly':      { multiplier: 12, label: { en: 'Monthly', fr: 'Mensuel' } },
        'biMonthly':    { multiplier: 6,  label: { en: 'Bimonthly', fr: 'Bimestriel' } },
        'quarterly':    { multiplier: 4,  label: { en: 'Quarterly', fr: 'Trimestriel' } },
        'semiAnnually': { multiplier: 2,  label: { en: 'Semiannually', fr: 'Semestriel' } },
        'annually':     { multiplier: 1,  label: { en: 'Annually', fr: 'Annuel' } },
      },

      // this will be used for the calculations and GTM
      frequencySelector: null,

      translations: {
        donationAmountLabel: { en: 'Donation Amount', fr: 'Montant du don' },
        totalDonatedLabel: { en: 'Total Donated', fr: 'Total donné' },
        provinceLabel: { en: 'Province/Territory', fr: 'Province/Territoire' },
        federalCreditLabel: { en: 'Federal Credit', fr: 'Crédit fédéral' },
        provincialCreditLabel: { en: 'Provincial Credit', fr: 'Crédit provincial' },
        totalCreditLabel: { en: 'Total Credit', fr: 'Crédit total' },
        selectProvincePlaceholder: { en: 'Select a province or territory', fr: 'Choisissez une province ou un territoire' },
        frequencyLabel: { en: 'How often do you donate?', fr: 'À quelle fréquence faites-vous un don?' },
        selectFrequencyPlaceholder: { en: 'Select a frequency', fr: 'Sélectionnez une fréquence' },
        resultsTitle: { en: 'Results', fr: 'Résultats' },
        calculateButton: { en: 'Calculate', fr: 'Calculer' },
        donationPlaceholder: { en: 'Enter amount', fr: 'Saisir le montant' },
        // Tooltip translations - revert if does not work on sitecore
        donationToolTip: { en: `<h3>Disclaimer</h3><p>This calculator estimates the non-refundable tax credit for eligible donations based on your province or territory of residence and donation amount. It does not account for all tax situations, including higher rates for top income brackets or recent tax changes. Since calculations depend on the accuracy of your entries, please ensure they are correct. As a non-refundable credit, it reduces tax owed but does not result in a refund if no tax is due.  Additional restrictions may apply based on income and provincial considerations. For the latest updates visit <a href="https://www.canada.ca/en/revenue-agency/services/charities-giving/giving-charity-information-donors.html" target="_blank">Canada Revenue Agency</a> or speak to a financial advisor, for more information about eligible donations and receipting, visit our <a href="https://cancer.ca/en/about-us/faqs" target="_blank">FAQ</a> page.</p>`, 
                           fr: `<h3>Avis de non-responsabilité</h3><p>Ce calculateur estime le crédit d’impôt non remboursable pour les dons admissibles, en fonction de votre province ou territoire de résidence et du montant de votre don. Il ne tient pas compte de toutes les situations fiscales, notamment les taux plus élevés applicables aux revenus élevés ou les modifications récentes de la fiscalité. Comme les calculs dépendent de l’exactitude des données saisies, veuillez vous assurer que celles-ci sont correctes. Étant un crédit non remboursable, il réduit l’impôt à payer, mais ne donne pas lieu à un remboursement si aucun impôt n’est dû. Des restrictions supplémentaires peuvent s’appliquer selon le revenu et les règles provinciales. Pour les dernières mises à jour, visitez le site de l’<a href="https://www.canada.ca/fr/agence-revenu/services/organismes-bienfaisance-dons/faire-bienfaisance-renseignements-a-intention-donateurs.html" target="_blank">Agence du revenu du Canada</a> ou consultez un conseiller financier. Pour en savoir plus sur les dons admissibles et les reçus fiscaux, consultez notre page <a href="https://cancer.ca/fr/about-us/faqs" target="_blank">FAQ.</a></p>` },
      },
      openTooltipIcon: `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></svg>
      `,
    }
  },
  mounted() {

    const savedProvince = sessionStorage.getItem('selectedProvince');
    if(savedProvince){
      RegionStore.selectedProvince = savedProvince;
      sessionStorage.removeItem('selectedProvince');
    }

    this.locale = this.language === 'fr' ? 'fr-CA' : 'en-CA';
    //pre-select the province if it comes from sitecore
    if (Object.keys(this.provinces).includes(this.actualprovince)) {
      this.province = this.actualprovince;
    }
    // pre-select the frequency if it comes from sitecore
    if (!this.frequencySelector && this.parsedFrequencyArray.length) {
      this.frequencySelector = this.parsedFrequencyArray[0];
    }
  },
  computed: {
    actualprovince(){
      return RegionStore.selectedProvince;
   },
    isProvinceSelected() {
      return this.province && Object.keys(this.provinces).includes(this.province);
    },
    //get the frequency options based on the frequency prop - they will come as an string or an array
    parsedFrequencyArray() {
      if (Array.isArray(this.frequencylist)) {
        return this.frequencylist;
      } else if (typeof this.frequencylist === 'string') {
        return this.frequencylist.split(',').map(f => f.trim());
      }
      return [];
    },
    totalDonated() {
      // just used to translate how the currency is displayed EN/FR
      return this.donation || 0;
    },
    // calculations are ('base percentage' x 'first $200') + ('amount above $200' x 'over $200 percentage')
    federalCredit() {
      // calculate and translate how the currency is displayed EN/FR
      if (!this.isProvinceSelected) return 0;
      const base = Math.min(this.donation, 200);
      const above = Math.max(0, this.donation - 200);
      const highIncomeRate = 0.29;
      return base * 0.15 + above * highIncomeRate;
    },
    provincialCredit() {
      // calculate and translate how the currency is displayed EN/FR
      if (!this.isProvinceSelected) return 0;
      const { first, over } = this.provinces[this.province] || { first: 0, over: 0 };
      const base = Math.min(this.donation, 200);
      const above = Math.max(0, this.donation - 200);
      return base * first + above * over;
    },
    totalCredit() {
      // just used to translate how the currency is displayed EN/FR
      return this.federalCredit + this.provincialCredit;
    }
  },
  methods: {
    formatCurrency(value) {
      // this is the function that formats the currency - it uses the locale
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: 'CAD'
      }).format(value);
    },
    translation(key) {
      return this.translations[key][this.language === 'fr' ? 'fr' : 'en'];
    },
    preventNonNumeric(event) {
      if (!/[0-9]/.test(event.key)) {
        event.preventDefault();
      }
    },
    getFrequencyLabel(key) {
      return this.frequencyObject[key]?.label[this.language] || '';
    },
    calculateCredits() {
      //calculate frequency multiplyer
      const frequencyMultiplier = this.frequencyObject[this.frequencySelector]
        ? this.frequencyObject[this.frequencySelector].multiplier
        : 1;
      this.donation = this.donationInput * frequencyMultiplier || 0;
      this.showResults = true;

      // DataLayer push for Google Tag Manager
      window.dataLayer = window.dataLayer || [];

      const eventData = {
        event: 'calculateButtonClick',
        calcAction: 'Calculate',
        calcLanguage: this.language,
        calcProvince: this.province,
        calcDonationAmount: this.donation,
        calcDonationFrequency: this.frequencySelector,
        calcTotalCredit: this.totalCredit,
        calcPageUrl: window.location.href
      };

      window.dataLayer.push(eventData);

      // Log to console for debugging
      // console.log('dataLayer push:', eventData);
      // console.log('Current dataLayer:', window.dataLayer);
    }
  }
};
</script>
